import { Employee } from "../../employees/hooks/useEmployees";

export type WellbeingScenarioType =
  | "PLUS_HOURS_HIGH"
  | "MINUS_HOURS_LOW"
  | "LONG_SICK_LEAVE"
  | "SICK_LEAVES_12M_HIGH"
  | "SHORT_SICK_LEAVES_3M_HIGH"
  | "VACATION_NOT_TAKEN"
  | "BURNOUT_RISK_HIGH"
  | "LOW_ENGAGEMENT"
  | "CONSECUTIVE_DAYS_HIGH";

export type WellbeingSeverity = "medium" | "high";

export type WellbeingScenario = {
  type: WellbeingScenarioType;
  severity: WellbeingSeverity;
  message: string;
  metricValue: number;
  threshold: number;
  unit: string;
};

export type WellbeingMetrics = {
  hourBalance: number;
  currentSickStreakDays: number;
  sickLeaves12m: number;
  shortSickLeaves3m: number;
  // Vacation metrics
  vacationDaysRemaining: number;
  vacationDaysTaken: number;
  daysSinceLastVacation: number;
  vacationUtilizationRate: number;
  // Work pattern metrics
  overtimeHoursLast4Weeks: number;
  consecutiveDaysWorked: number;
  weekendWorkInstances: number;
  meetingLoadScore: number;
  burnoutRiskScore: number;
  // Engagement metrics
  lastSurveyScore: number;
  surveyParticipationRate: number;
  sentimentTrend: "improving" | "stable" | "declining";
};

export type WellbeingEmployeeRow = {
  employee: Employee;
  scenarios: WellbeingScenario[];
  metrics: WellbeingMetrics;
  topSeverity: WellbeingSeverity;
};

const signalPriority: Record<WellbeingScenarioType, number> = {
  LONG_SICK_LEAVE: 1,
  BURNOUT_RISK_HIGH: 2,
  MINUS_HOURS_LOW: 3,
  CONSECUTIVE_DAYS_HIGH: 4,
  SICK_LEAVES_12M_HIGH: 5,
  SHORT_SICK_LEAVES_3M_HIGH: 6,
  LOW_ENGAGEMENT: 7,
  VACATION_NOT_TAKEN: 8,
  PLUS_HOURS_HIGH: 9
};

export const WELLBEING_THRESHOLDS = {
  plusHoursHigh: 40,
  minusHoursLow: -20,
  longSickLeaveDays: 14,
  sickLeaves12mHigh: 4,
  shortSickLeaves3mHigh: 3,
  vacationDaysRemaining: 15,
  burnoutRiskScore: 60,
  lowEngagementScore: 2,
  consecutiveDaysWorked: 12
};

const severityRank: Record<WellbeingSeverity, number> = {
  medium: 1,
  high: 2
};

const hashString = (value: string): number => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return hash >>> 0;
};

const mulberry32 = (seed: number) => {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const randomInt = (random: () => number, min: number, max: number) => {
  return Math.floor(random() * (max - min + 1)) + min;
};

export const getSimulationDayKey = (date = new Date()) => date.toISOString().slice(0, 10);

export const simulateWellbeingMetrics = (
  employeeId: string,
  dayKey: string = getSimulationDayKey()
): WellbeingMetrics => {
  const random = mulberry32(hashString(`${employeeId}:${dayKey}`));

  // Core metrics
  const hourBalance = randomInt(random, -60, 80);
  const currentSickStreakDays = randomInt(random, 0, 30);
  const sickLeaves12m = randomInt(random, 0, 10);
  const shortSickLeaves3m = randomInt(random, 0, 8);

  // Vacation metrics
  const vacationDaysRemaining = randomInt(random, 0, 30);
  const vacationDaysTaken = randomInt(random, 0, 25);
  const daysSinceLastVacation = randomInt(random, 0, 180);
  const vacationUtilizationRate = Math.round((vacationDaysTaken / (vacationDaysTaken + vacationDaysRemaining)) * 100);

  // Work pattern metrics
  const overtimeHoursLast4Weeks = randomInt(random, 0, 40);
  const consecutiveDaysWorked = randomInt(random, 0, 20);
  const weekendWorkInstances = randomInt(random, 0, 8);
  const meetingLoadScore = randomInt(random, 0, 100);

  // Calculate burnout risk based on multiple factors
  const burnoutFactors = [
    Math.abs(hourBalance) > 40 ? 20 : 0,
    overtimeHoursLast4Weeks > 20 ? 25 : 0,
    consecutiveDaysWorked > 10 ? 20 : 0,
    weekendWorkInstances > 4 ? 15 : 0,
    meetingLoadScore > 70 ? 20 : 0
  ];
  const burnoutRiskScore = Math.min(100, burnoutFactors.reduce((a, b) => a + b, 0));

  // Engagement metrics
  const lastSurveyScore = randomInt(random, 1, 5);
  const surveyParticipationRate = randomInt(random, 60, 100);
  const sentimentTrends: Array<"improving" | "stable" | "declining"> = ["improving", "stable", "declining"];
  const sentimentTrend = sentimentTrends[randomInt(random, 0, 2)];

  return {
    hourBalance,
    currentSickStreakDays,
    sickLeaves12m,
    shortSickLeaves3m,
    vacationDaysRemaining,
    vacationDaysTaken,
    daysSinceLastVacation,
    vacationUtilizationRate,
    overtimeHoursLast4Weeks,
    consecutiveDaysWorked,
    weekendWorkInstances,
    meetingLoadScore,
    burnoutRiskScore,
    lastSurveyScore,
    surveyParticipationRate,
    sentimentTrend
  };
};

export const evaluateWellbeingScenarios = (metrics: WellbeingMetrics): WellbeingScenario[] => {
  const scenarios: WellbeingScenario[] = [];

  if (metrics.hourBalance > WELLBEING_THRESHOLDS.plusHoursHigh) {
    scenarios.push({
      type: "PLUS_HOURS_HIGH",
      severity: metrics.hourBalance > 60 ? "high" : "medium",
      message: "High positive hour balance",
      metricValue: metrics.hourBalance,
      threshold: WELLBEING_THRESHOLDS.plusHoursHigh,
      unit: "h"
    });
  }

  if (metrics.hourBalance < WELLBEING_THRESHOLDS.minusHoursLow) {
    scenarios.push({
      type: "MINUS_HOURS_LOW",
      severity: metrics.hourBalance < -40 ? "high" : "medium",
      message: "Low negative hour balance",
      metricValue: metrics.hourBalance,
      threshold: WELLBEING_THRESHOLDS.minusHoursLow,
      unit: "h"
    });
  }

  if (metrics.currentSickStreakDays >= WELLBEING_THRESHOLDS.longSickLeaveDays) {
    scenarios.push({
      type: "LONG_SICK_LEAVE",
      severity: metrics.currentSickStreakDays >= 21 ? "high" : "medium",
      message: "Long current sick leave",
      metricValue: metrics.currentSickStreakDays,
      threshold: WELLBEING_THRESHOLDS.longSickLeaveDays,
      unit: "days"
    });
  }

  if (metrics.sickLeaves12m >= WELLBEING_THRESHOLDS.sickLeaves12mHigh) {
    scenarios.push({
      type: "SICK_LEAVES_12M_HIGH",
      severity: metrics.sickLeaves12m >= 7 ? "high" : "medium",
      message: "High number of sick leaves in 12 months",
      metricValue: metrics.sickLeaves12m,
      threshold: WELLBEING_THRESHOLDS.sickLeaves12mHigh,
      unit: "times"
    });
  }

  if (metrics.shortSickLeaves3m >= WELLBEING_THRESHOLDS.shortSickLeaves3mHigh) {
    scenarios.push({
      type: "SHORT_SICK_LEAVES_3M_HIGH",
      severity: metrics.shortSickLeaves3m >= 5 ? "high" : "medium",
      message: "High number of short sick leaves in 3 months",
      metricValue: metrics.shortSickLeaves3m,
      threshold: WELLBEING_THRESHOLDS.shortSickLeaves3mHigh,
      unit: "times"
    });
  }

  if (metrics.vacationDaysRemaining >= WELLBEING_THRESHOLDS.vacationDaysRemaining && metrics.daysSinceLastVacation > 90) {
    scenarios.push({
      type: "VACATION_NOT_TAKEN",
      severity: metrics.vacationDaysRemaining >= 20 ? "high" : "medium",
      message: "High unused vacation balance",
      metricValue: metrics.vacationDaysRemaining,
      threshold: WELLBEING_THRESHOLDS.vacationDaysRemaining,
      unit: "days"
    });
  }

  if (metrics.burnoutRiskScore >= WELLBEING_THRESHOLDS.burnoutRiskScore) {
    scenarios.push({
      type: "BURNOUT_RISK_HIGH",
      severity: metrics.burnoutRiskScore >= 80 ? "high" : "medium",
      message: "High burnout risk detected",
      metricValue: metrics.burnoutRiskScore,
      threshold: WELLBEING_THRESHOLDS.burnoutRiskScore,
      unit: "/100"
    });
  }

  if (metrics.lastSurveyScore <= WELLBEING_THRESHOLDS.lowEngagementScore) {
    scenarios.push({
      type: "LOW_ENGAGEMENT",
      severity: metrics.lastSurveyScore === 1 ? "high" : "medium",
      message: "Low engagement score",
      metricValue: metrics.lastSurveyScore,
      threshold: WELLBEING_THRESHOLDS.lowEngagementScore,
      unit: "/5"
    });
  }

  if (metrics.consecutiveDaysWorked >= WELLBEING_THRESHOLDS.consecutiveDaysWorked) {
    scenarios.push({
      type: "CONSECUTIVE_DAYS_HIGH",
      severity: metrics.consecutiveDaysWorked >= 15 ? "high" : "medium",
      message: "Extended period without time off",
      metricValue: metrics.consecutiveDaysWorked,
      threshold: WELLBEING_THRESHOLDS.consecutiveDaysWorked,
      unit: "days"
    });
  }

  return scenarios;
};

export const simulateWellbeingRows = (
  employees: Employee[],
  dayKey: string = getSimulationDayKey(),
  maxEmployees: number = 15
): WellbeingEmployeeRow[] => {
  const pickEmployees = (all: Employee[]) => {
    if (all.length <= maxEmployees) return all;

    // Deterministic daily random order to keep mockup stable during the day.
    const random = mulberry32(hashString(`wellbeing:${dayKey}`));
    const shuffled = [...all];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, maxEmployees);
  };

  const employeesWithId = pickEmployees(employees).filter(
    (employee): employee is Employee & { id: string } =>
      typeof employee.id === "string" && employee.id.length > 0
  );

  const rows: WellbeingEmployeeRow[] = [];

  for (const employee of employeesWithId) {
    const metrics = simulateWellbeingMetrics(employee.id, dayKey);
    const scenarios = evaluateWellbeingScenarios(metrics);

    if (scenarios.length === 0) continue;

    const topSeverity = scenarios.reduce((top, scenario) => {
      return severityRank[scenario.severity] > severityRank[top] ? scenario.severity : top;
    }, "medium" as WellbeingSeverity);

    rows.push({
      employee,
      metrics,
      scenarios,
      topSeverity
    });
  }

  return rows;
};

export const sortSignalsByPriority = (signals: WellbeingScenario[]): WellbeingScenario[] => {
  return [...signals].sort((a, b) => {
    if (severityRank[a.severity] !== severityRank[b.severity]) {
      return severityRank[b.severity] - severityRank[a.severity];
    }
    return signalPriority[a.type] - signalPriority[b.type];
  });
};

export const formatPrimaryRisk = (signal: WellbeingScenario): string => {
  switch (signal.type) {
    case "LONG_SICK_LEAVE":
      return `Long sick leave: ${signal.metricValue}${signal.unit} (>=${signal.threshold}${signal.unit})`;
    case "MINUS_HOURS_LOW":
      return `Hours below threshold: ${signal.metricValue}${signal.unit} (<${signal.threshold}${signal.unit})`;
    case "SICK_LEAVES_12M_HIGH":
      return `Sick leaves (12m): ${signal.metricValue} (>=${signal.threshold})`;
    case "SHORT_SICK_LEAVES_3M_HIGH":
      return `Short sick leaves (3m): ${signal.metricValue} (>=${signal.threshold})`;
    case "PLUS_HOURS_HIGH":
      return `Hours above threshold: +${signal.metricValue}${signal.unit} (>+${signal.threshold}${signal.unit})`;
    case "VACATION_NOT_TAKEN":
      return `Unused vacation: ${signal.metricValue}${signal.unit} remaining (>=${signal.threshold}${signal.unit})`;
    case "BURNOUT_RISK_HIGH":
      return `Burnout risk: ${signal.metricValue}${signal.unit} (>=${signal.threshold}${signal.unit})`;
    case "LOW_ENGAGEMENT":
      return `Low engagement: ${signal.metricValue}${signal.unit} (≤${signal.threshold}${signal.unit})`;
    case "CONSECUTIVE_DAYS_HIGH":
      return `Consecutive work days: ${signal.metricValue}${signal.unit} (>=${signal.threshold}${signal.unit})`;
    default:
      return signal.message;
  }
};

export const buildWellbeingLaunchRequest = (employeeId: string, workflowId: string) => ({
  employeeId,
  workflowId,
  draft: false,
  automationData: {},
  resources: []
});

// Historical trend data generation
export type WellbeingTrendDataPoint = {
  date: string;
  totalAtRisk: number;
  highSeverity: number;
  mediumSeverity: number;
  signalCounts: Record<WellbeingScenarioType, number>;
};

export const generateHistoricalTrends = (
  employees: Employee[],
  daysBack: number = 30
): WellbeingTrendDataPoint[] => {
  const today = new Date();
  const trends: WellbeingTrendDataPoint[] = [];

  for (let i = daysBack - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dayKey = getSimulationDayKey(date);

    const rows = simulateWellbeingRows(employees, dayKey);

    const signalCounts: Record<WellbeingScenarioType, number> = {
      PLUS_HOURS_HIGH: 0,
      MINUS_HOURS_LOW: 0,
      LONG_SICK_LEAVE: 0,
      SICK_LEAVES_12M_HIGH: 0,
      SHORT_SICK_LEAVES_3M_HIGH: 0,
      VACATION_NOT_TAKEN: 0,
      BURNOUT_RISK_HIGH: 0,
      LOW_ENGAGEMENT: 0,
      CONSECUTIVE_DAYS_HIGH: 0
    };

    let highSeverity = 0;
    let mediumSeverity = 0;

    rows.forEach(row => {
      if (row.topSeverity === "high") highSeverity++;
      else mediumSeverity++;

      row.scenarios.forEach(scenario => {
        signalCounts[scenario.type]++;
      });
    });

    trends.push({
      date: dayKey,
      totalAtRisk: rows.length,
      highSeverity,
      mediumSeverity,
      signalCounts
    });
  }

  return trends;
};

// Aggregate statistics for overview cards
export type WellbeingAggregateStats = {
  totalEmployees: number;
  totalAtRisk: number;
  highSeverityCount: number;
  mediumSeverityCount: number;
  averageWellbeingScore: number;
  trendDirection: "up" | "down" | "stable";
  signalDistribution: Array<{ type: WellbeingScenarioType; count: number }>;
};

export const calculateAggregateStats = (
  employees: Employee[],
  rows: WellbeingEmployeeRow[],
  historicalTrends: WellbeingTrendDataPoint[]
): WellbeingAggregateStats => {
  const highSeverityCount = rows.filter(r => r.topSeverity === "high").length;
  const mediumSeverityCount = rows.filter(r => r.topSeverity === "medium").length;

  // Calculate average wellbeing score (0-100, higher is better)
  const averageWellbeingScore = Math.round(
    ((employees.length - rows.length) / employees.length) * 100
  );

  // Determine trend direction
  let trendDirection: "up" | "down" | "stable" = "stable";
  if (historicalTrends.length >= 7) {
    const lastWeekAvg = historicalTrends.slice(-7).reduce((sum, d) => sum + d.totalAtRisk, 0) / 7;
    const prevWeekAvg = historicalTrends.slice(-14, -7).reduce((sum, d) => sum + d.totalAtRisk, 0) / 7;
    if (lastWeekAvg > prevWeekAvg * 1.1) trendDirection = "down"; // More at risk = trend down
    else if (lastWeekAvg < prevWeekAvg * 0.9) trendDirection = "up"; // Fewer at risk = trend up
  }

  // Signal distribution
  const signalCounts: Record<WellbeingScenarioType, number> = {
    PLUS_HOURS_HIGH: 0,
    MINUS_HOURS_LOW: 0,
    LONG_SICK_LEAVE: 0,
    SICK_LEAVES_12M_HIGH: 0,
    SHORT_SICK_LEAVES_3M_HIGH: 0,
    VACATION_NOT_TAKEN: 0,
    BURNOUT_RISK_HIGH: 0,
    LOW_ENGAGEMENT: 0,
    CONSECUTIVE_DAYS_HIGH: 0
  };

  rows.forEach(row => {
    row.scenarios.forEach(scenario => {
      signalCounts[scenario.type]++;
    });
  });

  const signalDistribution = Object.entries(signalCounts)
    .map(([type, count]) => ({ type: type as WellbeingScenarioType, count }))
    .filter(s => s.count > 0)
    .sort((a, b) => b.count - a.count);

  return {
    totalEmployees: employees.length,
    totalAtRisk: rows.length,
    highSeverityCount,
    mediumSeverityCount,
    averageWellbeingScore,
    trendDirection,
    signalDistribution
  };
};
